import { Box, useColorModeValue } from "@chakra-ui/react";
import React, { KeyboardEvent, useEffect } from "react";
import { darkMode, lightMode } from "../../../lib/models/color-mode-colors";
import { SelectItem } from "../../../lib/types/SelectItem";
import OptionInput from "./OptionInput";
import OptionList from "./OptionList";
import useAsyncSearch from "./useAsyncSearch";

export interface ComboBoxProps {
  label: string;
  items: SelectItem[];
  itemSelected?: SelectItem | null;
  showClearButton?: Boolean;
  onSelect: (item: SelectItem | null) => void;
  onSearch?: (query: string, callback: (options?: any) => void) => any;
  placeholder?: string;
  isDisabled?: boolean;
}

function ComboBox(props: ComboBoxProps) {
  const {
    label,
    items,
    itemSelected = null,
    showClearButton = true,
    onSelect,
    onSearch,
    placeholder,
    isDisabled,
  } = props;

  const bgColor = useColorModeValue(lightMode[100], darkMode[200]);
  const [selectedItem, setSelectedItem] = React.useState<SelectItem | null>(
    itemSelected ?? null
  );
  const [cursor, setCursor] = React.useState<number>(0);
  const [inputValue, setInputValue] = React.useState<string>("");
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const [isLoading, searchItems] = useAsyncSearch<SelectItem>(
    inputValue,
    onSearch,
    items
  );
  const [options, setOptions] = React.useState<SelectItem[]>(items);

  const inputRef = React.createRef<HTMLInputElement>();

  useEffect(() => {
    if (searchItems.length <= 1 && !inputValue) {
      setOptions(items);
    } else {
      setOptions(searchItems);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue, searchItems]);

  useEffect(() => {
    setCursor(0);
  }, [searchItems]);

  const onItemSelect = (index: number) => {
    if (index === -1) return;

    const item = searchItems[index];

    if (item) {
      setSelectedItem(item);
      setInputValue(item.label);
      setIsOpen(false);
      onSelect(item);
    }
  };

  const onSelectedItem = (item: SelectItem | null) => {
    setSelectedItem(item);
    setInputValue(item?.label ?? "");
    setIsOpen(false);
    onSelect(item);
  };

  const clearSelectedItem = () => {
    setInputValue("");
    onSelectedItem(null);
    inputRef.current && inputRef.current.focus();
  };

  const handleClose = () => {
    if (!inputValue) {
      setIsOpen(false);
      return;
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    e.stopPropagation();

    const keyPressed = e.key;

    switch (keyPressed) {
      case "ArrowDown":
        if (!isOpen) {
          setIsOpen(true);
          break;
        }
        if (cursor + 1 > items.length - 1) setCursor(0);
        else setCursor(cursor + 1);
        break;

      case "ArrowUp":
        if (cursor - 1 < 0) setCursor(items.length - 1);
        else setCursor(cursor - 1);
        break;

      case "Enter":
        isOpen && onItemSelect(cursor);
        e.preventDefault();
        break;

      default:
        if (!isOpen) setIsOpen(true);
        break;
    }
  };

  React.useEffect(() => {
    if (!isOpen && selectedItem) {
      setInputValue(selectedItem.label);
    }

    if (isOpen && selectedItem) {
      setInputValue("");
    }
  }, [isOpen, selectedItem]);

  React.useEffect(() => {
    if (!itemSelected) {
      setInputValue("");
      setSelectedItem(null);
      return;
    } else {
      setSelectedItem(itemSelected);
      setInputValue(itemSelected.label);
    }
  }, [itemSelected, items]);

  const handleInput = (query: string) => {
    setInputValue(query);
  };

  return (
    <Box position="relative" onKeyDown={handleKeyDown}>
      <OptionInput
        inputRef={inputRef}
        label={label}
        selectedItem={selectedItem}
        showClearButton={showClearButton}
        handleClearSelection={clearSelectedItem}
        handleInputBlur={handleClose}
        handleInputClick={() => setIsOpen(true)}
        handleInputChange={handleInput}
        placeholder={placeholder}
        isDisabled={isDisabled}
      />

      {isOpen && (
        <Box
          className="list"
          position="absolute"
          background={bgColor}
          zIndex="46"
          tabIndex={-1}
          width="full"
          marginTop={4}
          borderWidth={1}
          borderRadius="md"
          maxHeight="350px"
          overflow="auto"
          boxShadow="md"
          paddingY={2}
          onMouseDown={(e) => {
            e.preventDefault();
          }}
        >
          <OptionList
            cursor={cursor}
            selectedItem={selectedItem}
            items={options}
            handleItemClick={onSelectedItem}
            isLoading={isLoading}
          />
        </Box>
      )}
    </Box>
  );
}

export default ComboBox;
